import { configTracking } from '@/config/configTracking.ts';
import { getNinjaConfig } from '@/config/ninja';
import { CUSTOM_EVENTS, GENERAL_EVENTS, INTERNAL_EVENTS } from '@/const/events';
import { LAQUESIS_COOKIE_NAMES } from '@/const/laquesis';
import { cookieStorage } from '@/cookies/cookieStorage';
import { getCookieExpirationDate } from '@/cookies/getCookieExpirationDate';
import { getLQDefinition, writeLQDefinition } from '@/cookies/laquesis/lqstatus';
import { parseLaquesisCookie, parseLaquesisFfCookie } from '@/cookies/laquesis/parse';
import { ninjaBus } from '@/eventbus';
import { getLogger } from '@/logger';
import { state } from '@/ninja/state';
import { BaseTracker } from '@/trackers/BaseTracker';
import { EventHandler } from '@/types/events';
import { triggerCustomEvent } from '@/utils/event';
import { checkResults, overrideLaquesisResultsPush } from './cookieWatcher';
import { reloadLaquesisDefinitions } from './definition';
import { trackExpsAssignment, trackFlagsAssignment, trackImpression } from './laquesisTracking';
import { handlePlaygroundParams } from './playground';
import { setupNativeWindow, setupWindow } from './setupWindow';

const logger = getLogger('Laquesis');

export class Laquesis extends BaseTracker {
  listeners: Record<string, EventHandler>;

  constructor() {
    super();
    this.trackerName = 'laquesis';
    this.listeners = {
      [INTERNAL_EVENTS.DEFINITION_RELOAD]: async () => reloadLaquesisDefinitions(),
      [INTERNAL_EVENTS.TRACK_EXP_ASSIGNMENT]: () => {
        trackExpsAssignment();
      },
      [INTERNAL_EVENTS.TRACK_FLAG_ASSIGNMENT]: () => {
        trackFlagsAssignment();
      },
      [INTERNAL_EVENTS.TRACK_IMPRESSION]: e => {
        trackImpression(e.props.expName as string, e.props.variantName as string);
      },
      [GENERAL_EVENTS.FIRST_PAGE_TRACKED]: () => {
        checkResults(parseLaquesisCookieCallback);
      },
    };
  }

  async init() {
    if (this.state === 'not_loaded') {
      try {
        if (getNinjaConfig().isNative) {
          setupNativeWindow();
          logger.debug('Native window was set up');
        } else {
          if (!state.currentSession?.sessionLong) {
            logger.debug('No session long available. Laquesis initialization aborted');
            return;
          }

          await initLaquesis();

          setupWindow();

          // Call function when laquesis is loaded and ready to use
          // THIS IS USED IN react-laquesis!
          if (typeof window.laquesisFunctionsCallback === 'function') {
            logger.debug('Calling laquesisFunctionsCallback()', window.laquesisFunctionsCallback);
            window.laquesisFunctionsCallback();
          }

          // Handle cookie watcher
          overrideLaquesisResultsPush(parseLaquesisCookieCallback);
          // checkResults(parseLaquesisCookieCallback);

          triggerCustomEvent(CUSTOM_EVENTS.LAQUESIS_READY);
          logger.debug('Laquesis initialized');

          // Check the status every minute to ensure the definitions are refreshed regularly
          scheduleRefreshCheck();
        }

        this.state = 'ready';
      } catch (err) {
        this.state = 'error';
        logger.error('Laquesis initialization error', err);
      }
    }

    return this;
  }

  async setupListeners() {
    if (!getNinjaConfig().isNative) {
      await super.setupListeners();
    }
    await this.init();

    return this;
  }

  async trackEvent() {}
  async trackPage() {}
}

async function initLaquesis() {
  try {
    const laquesisQaHash = new URL(window.location.href).searchParams.get('laquesisqahash');
    if (laquesisQaHash) {
      logger.debug('Laquesis qa hash detected.');
      handlePlaygroundParams(laquesisQaHash);
    }

    if (configTracking.userId) {
      state.userId = configTracking.userId;
    }

    if (configTracking.userUUId) {
      state.userUUId = configTracking.userUUId;
    }

    ninjaBus.emit(INTERNAL_EVENTS.INIT_SURVEYS_STORAGE, '');

    checkLaquesisRefresh();
    if (state.isLQRefreshNeeded && !state.playgroundMode) {
      await reloadLaquesisDefinitions();
    } else {
      const expsCookieValue = cookieStorage.get(LAQUESIS_COOKIE_NAMES.LAQUESIS);
      if (expsCookieValue) {
        state.activeExps = parseLaquesisCookie(expsCookieValue, parseLaquesisCookieCallback);
        // New user, the cookie and definition comes from the backend
        if (state.cookieFromLq) {
          state.cookieFromLq = false;
          // Fix for some servers that quotes the cookie when contains @ :: Overwrite laquesis without quotes
          const normalizedCookieValue = expsCookieValue.replace(/^"(.+)"$/, '$1');
          cookieStorage.set(LAQUESIS_COOKIE_NAMES.LAQUESIS, normalizedCookieValue, {
            expires: getCookieExpirationDate(1, 'year'),
            path: '/',
            domain: getNinjaConfig().cookieDomain,
          });
          ninjaBus.emit(GENERAL_EVENTS.TRACK_FEATURE_USAGE, '', { message: 'laquesis/lqonap' });
          trackExpsAssignment();
        }
      }
      const flagsCookieValue = cookieStorage.get(LAQUESIS_COOKIE_NAMES.LAQUESIS_FF);
      if (flagsCookieValue) {
        state.activeFlags = parseLaquesisFfCookie(flagsCookieValue);
      }
    }
  } catch (error) {
    logger.error('Laquesis initialization error', error);
    throw error;
  }
}

function scheduleRefreshCheck() {
  setTimeout(async () => {
    checkLaquesisRefresh();
    if (state.isLQRefreshNeeded) {
      await initLaquesis();
    }
    scheduleRefreshCheck();
  }, 1000 * 60);
}

export function checkLaquesisRefresh() {
  const lqDefinition = getLQDefinition();
  if (lqDefinition.nextRefresh - Date.now() < 0) {
    // Refresh because there is no cookie yet or the definition is expired
    state.isLQRefreshNeeded = true;
  }
  if (lqDefinition.isDefinitionsForUserId !== !!state.userId) {
    // Refresh because there is a mismatch in identifiers used by the LQ definition and the SDK config (sessionLong vs. userId)
    state.isLQRefreshNeeded = true;
    lqDefinition.isDefinitionsForUserId = !!state.userId;
    writeLQDefinition(lqDefinition);
  }
  if (lqDefinition.isDefinitionsForUUId !== !!state.userUUId) {
    // Refresh because there is a mismatch in identifiers used by the LQ definition and the SDK config (sessionLong vs. userUUId)
    state.isLQRefreshNeeded = true;
    lqDefinition.isDefinitionsForUUId = !!state.userUUId;
    writeLQDefinition(lqDefinition);
  }
}

export function parseLaquesisCookieCallback(expName: string, variantName: string) {
  trackImpression(expName, variantName);
  // triggerCustomEvent(CUSTOM_EVENTS.LAQUESIS_EXP_IMPRESSION, {
  //   experiment: expName,
  //   variant: variantName,
  // });
}
